import { getApiGatewayUrl } from "@campgladiator/cg-common.config.config";
import {
  ProgramFullDTO,
  ProgramDTO,
  Request,
  ProgramScheduleChangeRequestDTO,
  ProgramScheduleChangeDTO,
  BaseEnumerableResponse,
} from "@campgladiator/cg-common.types.types";
import { fetchRequest } from "@campgladiator/cg-common.api.fetch-request";
import { generateQueryString } from "@campgladiator/cg-common.utility.objects";

type FetchProgramParams = {
  urlParameters: string;
};

type ProgramScheduleParams = {
  programScheduleId?: string;
  size?: number;
  page?: number;
};

export const fetchPrograms = async ({
  urlParameters,
}: FetchProgramParams): Promise<ProgramFullDTO | null> => {
  try {
    const request: Request = {
      requestConfig: {
        method: "GET",
        url: {
          baseUrl: getApiGatewayUrl(),
          endpoint: `/library/v1/programs/full${urlParameters}`,
        },
      },
    };
    const response = await fetchRequest({ request });
    const res = await response.json();
    return response.ok
      ? (res as ProgramFullDTO)
      : Promise.reject(new Error(res.error));
  } catch (error) {
    return null;
  }
};

export const fetchProgramById = async ({
  programId,
}: {
  programId: string;
}): Promise<ProgramDTO | null> => {
  try {
    const request: Request = {
      requestConfig: {
        method: "GET",
        url: {
          baseUrl: getApiGatewayUrl(),
          endpoint: `/library/v1/programs/${programId}`,
        },
      },
    };
    const response = await fetchRequest({ request });
    const res = await response.json();

    return response.ok
      ? Promise.resolve(res as ProgramDTO)
      : Promise.reject(res.message);
  } catch (error) {
    return null;
  }
};

export const createProgramScheduleChangeRequest = async (
  changes: ProgramScheduleChangeRequestDTO,
  token: string
): Promise<ProgramScheduleChangeDTO> => {
  const request: Request = {
    requestConfig: {
      method: "POST",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint: `/library/v1/program-schedules/changes`,
      },
      authToken: token,
    },
    body: JSON.stringify(changes),
  };
  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as ProgramScheduleChangeDTO;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const updateProgramScheduleChangeRequest = async (
  id: string,
  changes: ProgramScheduleChangeRequestDTO,
  token: string
): Promise<ProgramScheduleChangeDTO> => {
  const request: Request = {
    requestConfig: {
      method: "PUT",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint: `/library/v1/program-schedules/changes/${id}`,
      },
      authToken: token,
    },
    body: JSON.stringify(changes),
  };
  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as ProgramScheduleChangeDTO;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const deleteProgramScheduleChangeRequest = async (
  id: string,
  token: string
): Promise<void> => {
  const request: Request = {
    requestConfig: {
      method: "DELETE",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint: `/library/v1/program-schedules/changes/${id}`,
      },
      authToken: token,
    },
  };
  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";
    throw new Error(errorMessage as string);
  }
};

export const getProgramScheduleChangeRequest = async (
  args: ProgramScheduleParams,
  token: string
): Promise<BaseEnumerableResponse<ProgramScheduleChangeDTO>> => {
  const request: Request = {
    requestConfig: {
      method: "GET",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint: `/library/v1/program-schedules/changes`,
        params: args ? `?${generateQueryString(args)}` : undefined,
      },
      authToken: token,
    },
  };
  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as BaseEnumerableResponse<ProgramScheduleChangeDTO>;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";
    throw new Error(errorMessage as string);
  }
};
